import { useState } from 'react';
import { Button, Box, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Dialog } from '../../components/Dialog';
import LinearProgressWithLabel from '../../components/LinearProgressWithLabel/LinearProgressWithLabel';
import { Dropzone } from '../../components/Dropzone';
import * as api from '../../lib/api/elevation';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import { TextField } from '../../components/inputs/TextField';
import { usePermission } from '../../hooks/use-permission';

const resolutions = [2, 2.5, 3];
const versions = ['', '3.2.2', '3.2.1'];

ElevationDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export function ElevationDialog({ onCancel, onClose }) {
  const [uploadFile, setUploadFile] = useState();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [source, setSource] = useState('');
  const [resolution, setResolution] = useState(2.5);
  const [version, setVersion] = useState('');
  const growerId = useSelector(growerSelectors.selectedId);
  const isAdmin = usePermission('ADMIN_ELEVATION');

  return (
    <Dialog
      title="Generate Elevation"
      onCancel={onCancel}
      width="sm"
      actions={[
        <Button
          sx={{ mr: 1 }}
          onClick={onCancel}
          variant="contained"
          color="dark"
          key="cancel-btn"
        >
          Cancel
        </Button>,
        <Button
          sx={{ mr: 1 }}
          onClick={handleContinueClick}
          variant="contained"
          color="primary"
          disabled={isUploading}
          key="upload-btn"
        >
          Continue
        </Button>,
      ]}
    >
      {buildVersion()}
      {buildSource()}
      {buildResolution()}
      {buildUploader()}
    </Dialog>
  );

  function buildSource() {
    return (
      <TextField
        select
        label="DEM Source"
        value={source}
        onChange={(e) => setSource(e.target.value)}
        sx={{ mt: 2 }}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">Fastest of the 3DEP sources to generate</MenuItem>
        <MenuItem value="custom">Custom</MenuItem>
        <MenuItem value="dem">3DEP DEM</MenuItem>
        <MenuItem value="lidar">3DEP LiDAR</MenuItem>
        <MenuItem value="illinois">
          Illinois Height Modernization (ILHMP) Lidar Data
        </MenuItem>
      </TextField>
    );
  }

  function buildVersion() {
    if (!isAdmin) return null;

    return (
      <TextField
        select
        label="R Version"
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        sx={{ mt: 2 }}
        SelectProps={{ displayEmpty: true }}
      >
        {versions.map((v, i) => (
          <MenuItem value={v} key={i}>
            {v || 'Latest'}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  function buildResolution() {
    if (source === 'custom') return null;

    return (
      <TextField
        select
        label="Resolution"
        value={resolution}
        onChange={(e) => setResolution(e.target.value)}
        sx={{ mt: 2 }}
      >
        {resolutions.map((res) => (
          <MenuItem value={res} key={res}>
            {`${res} meter`}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  function buildUploader() {
    if (source !== 'custom') return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Dropzone
          onChange={setFiles}
          fileType="tif"
          accept=".tif"
          maxFiles={1}
        />

        {!!uploadFile && (
          <Box key={uploadFile.name} sx={{ px: 3, pt: 3 }}>
            <div>{uploadFile.name}</div>
            <LinearProgressWithLabel value={uploadProgress} />
          </Box>
        )}
      </Box>
    );
  }

  function setFiles(files) {
    setUploadFile(files[0]);
  }

  function handleContinueClick(e) {
    uploadFiles(e).then((result) => onClose(result));
  }

  async function uploadFiles(e) {
    e.preventDefault();

    if (!uploadFile)
      return {
        version,
        resolution,
        sources: source ? [source] : undefined,
      };

    setIsUploading(true);

    return api
      .getElevationUploadUrl(growerId, uploadFile.name)
      .then(uploadElevation)
      .catch((err) => {
        setIsUploading(false);
        throw err;
      });
  }

  async function uploadElevation(presignedUrlObj) {
    await api.uploadElevation(
      presignedUrlObj.uploadUrl,
      uploadFile,
      handleUploadProgress,
    );

    return { version, customDemUrl: presignedUrlObj.s3Url };
  }

  function handleUploadProgress(progressEvent) {
    setUploadProgress((progressEvent.loaded / progressEvent.total) * 100);
  }
}
